import React, { useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { alpha, Box, FormControl, FormLabel, Link, Stack, Switch, Theme, Typography } from '@mui/material';
import { FormControlProps, FormElementControlPropsType } from 'components/common/form/layout/control';
import { FormComponentBuilder } from 'components/common/form/layout';
import IconMi from 'components/common/icon/IconMi';
import { useEntityIssues } from 'hooks/useEntityIssues';
import { useEditPanelBridge } from './EditPanelBridge';
import useBundleTranslation from 'i18n';
import { YNValues } from 'tools/types';

export interface DataQuestionsToggleProps extends FormControlProps {
    elementId: number;
    modelTerm: string;
    resourceTypeField: string;
    kgEnableWithClarifications: YNValues;
}

interface ToggleError {
    inconsistencies: boolean;
    clarifications: boolean;
    attempt: number;
}

const ERROR_AUTO_DISMISS_MS = 6000;

// §4.1 "Available for data questions": gated on 0 unresolved inconsistencies and
// (KG_ENABLE_WITH_CLARIFICATIONS == 'Y' or 0 unanswered clarifications), §7.3
export default function DataQuestionsToggle({
    controlProps: props,
}: FormElementControlPropsType<DataQuestionsToggleProps>) {
    const { t } = useBundleTranslation('app/content-workflow/element-editor');
    const bridge = useEditPanelBridge();
    // Shares the react-query cache with the Issues tab — no duplicate fetch
    const issues = useEntityIssues(props.elementId, 'element', props.elementId > 0);

    const [error, setError] = useState<ToggleError | null>(null);

    // useWatch subscribes only this component — form-level hookFormWatch would re-render
    // the whole form root on every change of these fields
    const enabled = useWatch({ control: props.form.hookFormControl, name: props.name }) === 'Y';
    const resourceType = useWatch({ control: props.form.hookFormControl, name: props.resourceTypeField });
    // §4.1 edge case: Talk To Your Data is already configured against a dataset/domain —
    // render disabled, never silently re-point the primary resource to the model. The conflict
    // is independent of the toggle state: an off toggle with a non-model resource must stay
    // locked so flipping it on can't repoint the primary resource.
    const resourceTypeConflict = Boolean(resourceType) && resourceType !== 'model';

    const clarificationsCanBlock = props.kgEnableWithClarifications !== 'Y';
    const inconsistenciesBlock = issues.unresolvedCount > 0;
    const clarificationsBlock = clarificationsCanBlock && issues.unansweredCount > 0;

    // The error dismisses automatically after ~6 seconds
    useEffect(() => {
        if (!error) {
            return;
        }
        const timer = setTimeout(() => setError(null), ERROR_AUTO_DISMISS_MS);
        return () => clearTimeout(timer);
    }, [error]);

    // ...or immediately when the gating condition clears
    useEffect(() => {
        if (error && !inconsistenciesBlock && !clarificationsBlock) {
            setError(null);
        }
    }, [error, inconsistenciesBlock, clarificationsBlock]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.checked) {
            // Flipping off is always allowed; no validation runs (§4.1)
            props.form.hookFormSetValue(props.name, 'N', { shouldDirty: true });
            return;
        }

        if (resourceTypeConflict) {
            // Never silently re-point the primary resource away from its current configuration
            return;
        }

        if (inconsistenciesBlock || clarificationsBlock) {
            // Snap back: the value is never committed, so the switch appears to never have moved
            setError((prev) => ({
                inconsistencies: inconsistenciesBlock,
                clarifications: clarificationsBlock,
                attempt: (prev?.attempt ?? 0) + 1,
            }));
            if (inconsistenciesBlock) {
                bridge?.expandInconsistencies();
            }
            if (clarificationsBlock) {
                bridge?.expandClarifications();
            }
            return;
        }

        props.form.hookFormSetValue(props.name, 'Y', { shouldDirty: true });
        // Same state as enabling Talk To Your Data with the report's model as primary resource
        props.form.hookFormSetValue(props.resourceTypeField, 'model', { shouldDirty: true });
    };

    const reviewUrl = useMemo(
        () => issues.context?.inconsistencies[0]?.resolveUrl.split('#')[0] ?? '',
        [issues.context],
    );

    const errorKey = error
        ? error.inconsistencies && error.clarifications
            ? 'cant_enable_both'
            : error.inconsistencies
              ? 'cant_enable_inconsistencies'
              : 'cant_enable_clarifications'
        : '';

    return (
        <FormControl fullWidth>
            <FormLabel className="fw-bold">{t('available_for_data_questions')}</FormLabel>
            <Stack direction="row" alignItems="center" spacing={1}>
                <Switch
                    checked={enabled && !resourceTypeConflict}
                    onChange={handleChange}
                    disabled={resourceTypeConflict}
                    data-test="available_for_data_questions_toggle"
                />
                <Typography sx={{ fontSize: '12px', color: 'text.secondary', lineHeight: 1.5 }}>
                    {t('data_questions_helper', { modelTerm: props.modelTerm })}
                </Typography>
            </Stack>
            {resourceTypeConflict && (
                <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: 0.5 }}>
                    {t('data_questions_configured_elsewhere')}
                </Typography>
            )}
            {error && (
                <Box
                    key={error.attempt}
                    data-test="data_questions_toggle_error"
                    sx={{
                        mt: 1,
                        p: 1.25,
                        borderRadius: '3px',
                        borderLeft: '3px solid',
                        borderColor: 'error.main',
                        bgcolor: (theme: Theme) => alpha(theme.palette.error.main, 0.08),
                        '@keyframes miToggleErrorShake': {
                            '10%, 90%': { transform: 'translateX(-1px)' },
                            '20%, 80%': { transform: 'translateX(2px)' },
                            '30%, 50%, 70%': { transform: 'translateX(-3px)' },
                            '40%, 60%': { transform: 'translateX(3px)' },
                        },
                        animation: 'miToggleErrorShake 0.5s ease',
                    }}
                >
                    <Typography sx={{ fontSize: '12px', color: 'error.main', lineHeight: 1.5 }}>
                        {t(errorKey)}
                        {error.inconsistencies && reviewUrl && (
                            <>
                                {' '}
                                <Link
                                    href={reviewUrl}
                                    target="_blank"
                                    rel="noopener"
                                    sx={{ fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '3px' }}
                                    data-test="review_inconsistencies_link"
                                >
                                    {t('review_inconsistencies')}
                                    <IconMi icon="arrow-right" sx={{ fontSize: '12px' }} />
                                </Link>
                            </>
                        )}
                    </Typography>
                </Box>
            )}
        </FormControl>
    );
}

export class DataQuestionsToggleBuilder extends FormComponentBuilder {
    prepareProps(): DataQuestionsToggleProps {
        return {
            ...this.controlProps,
            elementId: this.elementProps.componentProps?.elementId,
            modelTerm: this.elementProps.componentProps?.modelTerm,
            resourceTypeField: this.elementProps.componentProps?.resourceTypeField,
            kgEnableWithClarifications: this.elementProps.componentProps?.kgEnableWithClarifications,
        };
    }
}
